<template>
    <SystemContainer>
        <ManageSidebar />
        <MainContentWrapper>
            <!-- * 헤더 섹션 -->
            <HeaderSection>
                <HeaderTitle>
                    <h1>시스템 관리</h1>
                    <span>시스템 설정 및 관리 기능을 제공합니다</span>
                </HeaderTitle>
            </HeaderSection>

            <CardsGrid>
                <!-- 담당자 목록 카드 -->
                <ManagementCard>
                    <CardHeader>
                        <AccountGroup :size="30"
                            :style="{ paddingTop: '2px', marginRight: '16px', color: '#0f4c5c' }" />
                        <CardTitle>담당자 목록</CardTitle>
                        <CardActions>
                            <ActionButton size="small" @click="openAddManagerModal">
                                <Plus :size="16" :style="{ paddingTop: '3px' }" />
                                추가
                            </ActionButton>
                        </CardActions>
                    </CardHeader>

                    <!-- 검색 섹션 -->
                    <SearchSection>
                        <SearchInput v-model="managerSearchQuery" placeholder="담당자명 검색" @keyup.enter="handleSearch" />
                        <SearchButton @click="handleSearch">
                            <Magnify :size="20" :style="{ paddingTop: '3px' }" />
                            검색
                        </SearchButton>
                    </SearchSection>

                    <!-- 담당자 테이블 -->
                    <TableContainer>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>번호</TableHeaderCell>
                                <TableHeaderCell>담당자명</TableHeaderCell>
                                <TableHeaderCell>담당자 아이디</TableHeaderCell>
                                <TableHeaderCell>부서</TableHeaderCell>
                                <TableHeaderCell>권한</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow v-for="(manager, index) in filteredManagers" :key="manager.id"
                                @click="editManager(manager)" style="cursor: pointer;"
                                :class="{ 'blurred': isTestUser }">
                                <TableCell>{{ index + 1 }}</TableCell>
                                <TableCell>
                                    <ManagerName>{{ manager.name }}</ManagerName>
                                </TableCell>
                                <TableCell>{{ manager.userId }}</TableCell>
                                <TableCell>{{ manager.department || '부서 미지정' }}</TableCell>
                                <TableCell>{{ manager.role === 'admin' ? '⭕' : '❌' }}</TableCell>
                            </TableRow>
                        </TableBody>
                    </TableContainer>
                </ManagementCard>

                <!-- 회사 정보 카드 -->
                <ManagementCard>
                    <CardHeader>
                        <OfficeBuilding :size="30"
                            :style="{ paddingTop: '2px', marginRight: '16px', color: '#0f4c5c' }" />
                        <CardTitle>회사 정보</CardTitle>
                        <CardActions>
                            <ActionButton size="small" @click="editCompany">
                                <Plus :size="16" :style="{ paddingTop: '3px' }" />
                                수정
                            </ActionButton>
                        </CardActions>
                    </CardHeader>

                    <!-- 회사 정보 카드 -->
                    <CompanyInfoGrid>
                        <div>
                            <CompanyCardHeader>
                                <CompanyHeaderInfo>
                                    <CompanyName :class="{ 'blurred': isTestUser }">{{ companyInfo.name || '회사명 미등록' }}
                                    </CompanyName>
                                </CompanyHeaderInfo>
                            </CompanyCardHeader>

                            <InfoRow>
                                <InfoLabel>대표자</InfoLabel>
                                <InfoValue :class="{ 'blurred': isTestUser }">{{ companyInfo.ceo_name || '미등록' }}
                                </InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>사업장 주소</InfoLabel>
                                <InfoValue :class="{ 'blurred': isTestUser }">{{ companyInfo.address || '미등록' }}
                                </InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>기관번호</InfoLabel>
                                <InfoValue :class="{ 'blurred': isTestUser }">{{ companyInfo.cnum || '미등록' }}
                                </InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>사업자등록번호</InfoLabel>
                                <InfoValue :class="{ 'blurred': isTestUser }">{{ formatNumber(companyInfo.bnum) || '미등록'
                                }}
                                </InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>공인인증서 아이디</InfoLabel>
                                <InfoValue :class="{ 'blurred': isTestUser }">{{ companyInfo.certificate_id || '미등록' }}
                                </InfoValue>
                            </InfoRow>

                            <CompanyCardFooter>
                                <DocumentStatusGrid>
                                    <DocumentStatusItem>
                                        <DocumentIcon v-if="!companyInfo.stamp_image_url" :has-document="false">🔐
                                        </DocumentIcon>
                                        <DocumentLabel v-if="!companyInfo.stamp_image_url">직인 등록</DocumentLabel>

                                        <ImageContainer v-if="companyInfo.stamp_image_url">
                                            <CompanyImage :class="{ 'blurred': isTestUser }"
                                                :src="companyInfo.stamp_image_url" alt="직인" />
                                        </ImageContainer>

                                        <ImageUploadButton v-else @click="openImageUpload('stamp')">
                                            이미지 등록
                                        </ImageUploadButton>

                                        <!-- 삭제 버튼은 admin만 보이도록 -->
                                        <DeleteImageButton v-if="companyInfo.stamp_image_url && checkAdminPermission()"
                                            @click="deleteCompanyImage('stamp')">
                                            ✕
                                        </DeleteImageButton>

                                        <input :id="`stampFileInput`" type="file" accept=".jpg,.jpeg,.png"
                                            style="display: none;" @change="handleImageUpload('stamp', $event)" />
                                    </DocumentStatusItem>

                                    <DocumentStatusItem>
                                        <DocumentIcon v-if="!companyInfo.business_card_image_url" :has-document="false">
                                            🪪</DocumentIcon>
                                        <DocumentLabel v-if="!companyInfo.business_card_image_url">등록증 등록
                                        </DocumentLabel>

                                        <ImageContainer v-if="companyInfo.business_card_image_url">
                                            <CompanyImage :src="companyInfo.business_card_image_url" alt="명함" />
                                        </ImageContainer>

                                        <ImageUploadButton v-else @click="openImageUpload('business_card')">
                                            이미지 등록
                                        </ImageUploadButton>

                                        <!-- 삭제 버튼은 admin만 보이도록 -->
                                        <DeleteImageButton
                                            v-if="companyInfo.business_card_image_url && checkAdminPermission()"
                                            @click="deleteCompanyImage('business_card')">
                                            ✕
                                        </DeleteImageButton>

                                        <input :id="`businessCardFileInput`" type="file" accept=".jpg,.jpeg,.png"
                                            style="display: none;"
                                            @change="handleImageUpload('business_card', $event)" />
                                    </DocumentStatusItem>

                                    <DocumentStatusItem>
                                        <DocumentIcon v-if="!companyInfo.logo_image_url" :has-document="false">📇
                                        </DocumentIcon>
                                        <DocumentLabel v-if="!companyInfo.logo_image_url">로고 등록</DocumentLabel>

                                        <ImageContainer v-if="companyInfo.logo_image_url">
                                            <CompanyImage :src="companyInfo.logo_image_url" alt="로고" />
                                        </ImageContainer>

                                        <ImageUploadButton v-else @click="openImageUpload('logo')">
                                            이미지 등록
                                        </ImageUploadButton>

                                        <!-- 삭제 버튼은 admin만 보이도록 -->
                                        <DeleteImageButton v-if="companyInfo.logo_image_url && checkAdminPermission()"
                                            @click="deleteCompanyImage('logo')">
                                            ✕
                                        </DeleteImageButton>

                                        <input :id="`logoFileInput`" type="file" accept=".jpg,.jpeg,.png"
                                            style="display: none;" @change="handleImageUpload('logo', $event)" />
                                    </DocumentStatusItem>


                                    <DocumentStatusItem>
                                        <DocumentIcon :has-document="!!companyInfo.ceo_phone">🔑</DocumentIcon>
                                        <DocumentLabel>API 등록</DocumentLabel>

                                        <!-- API가 등록되지 않은 경우에만 등록 버튼 표시 -->
                                        <ImageUploadButton v-if="!hasRegisteredApi" @click="openApiModal">
                                            API 등록
                                        </ImageUploadButton>

                                        <!-- API가 등록된 경우 연결된 API 개수와 관리 버튼 표시 -->
                                        <ApiStatusContainer v-else>
                                            <ApiStatusText>
                                                API 연결됨 ({{ registeredApiList.length }}개)
                                            </ApiStatusText>
                                            <ApiActions>
                                                <ApiManageBtn @click="openApiModal">
                                                    관리
                                                </ApiManageBtn>
                                            </ApiActions>
                                        </ApiStatusContainer>
                                    </DocumentStatusItem>
                                </DocumentStatusGrid>
                            </CompanyCardFooter>
                        </div>
                    </CompanyInfoGrid>
                </ManagementCard>

                <!-- * 공유 업체 관리 카드 -->
                <ManagementCard>
                    <CardHeader>
                        <ShareVariant :size="30"
                            :style="{ paddingTop: '2px', marginRight: '16px', color: '#0f4c5c' }" />
                        <CardTitle>공유 업체 관리</CardTitle>
                        <CardActions>
                            <ActionButton size="small" @click="openSharedCompanyModal">
                                <Plus :size="16" :style="{ paddingTop: '3px' }" />
                                공유업체등록/요청
                            </ActionButton>
                        </CardActions>
                    </CardHeader>

                    <TableContainer>
                        <PartnerTableHeader>
                            <PartnerTableHeaderCell>상태</PartnerTableHeaderCell>
                            <PartnerTableHeaderCell>업체명</PartnerTableHeaderCell>
                            <PartnerTableHeaderCell>삭제</PartnerTableHeaderCell>
                        </PartnerTableHeader>
                        <TableBody>
                            <PartnerTableRow v-for="(partner, index) in partners" :key="partner.id"
                                :class="{ 'blurred': isTestUser }">
                                <PartnerTableCell>
                                    <span :class="getStatusClass(partner.status)">
                                        {{ getStatusText(partner.status) }}
                                    </span>
                                </PartnerTableCell>
                                <PartnerTableCell>
                                    <PartnerName>{{ partner.name }}</PartnerName>
                                    <div style="font-size: 12px; color: #6c757d; margin-top: 4px;">
                                        {{ partner.code }}
                                    </div>
                                </PartnerTableCell>
                                <PartnerTableCell>
                                    <DeleteButton @click="deletePartner(partner.id)">삭제</DeleteButton>
                                </PartnerTableCell>
                            </PartnerTableRow>
                        </TableBody>
                    </TableContainer>
                </ManagementCard>
            </CardsGrid>
        </MainContentWrapper>

        <!-- * 담당자 모달 컴포넌트 -->
        <ManagerModal :show="showManagerModal" :is-edit="isEditMode" :manager-data="managerForm"
            @save="handleManagerSaved" @delete="handleManagerDeleted" @close="closeManagerModal" />

        <!-- * 회사 정보 모달 컴포넌트 -->
        <CompanyModal :show="showCompanyModal" :company-data="companyInfo" @save="handleCompanySaved"
            @close="closeCompanyModal" />

        <!-- * API 모달 컴포넌트 -->
        <ApiModal :show="showApiModal" @close="closeApiModal" @api-updated="handleApiUpdated" />

    </SystemContainer>
</template>

<script>
import ManageSidebar from './ManageSidebar.vue';
import ManagerModal from './modal/ManagerModal.vue';
import CompanyModal from './modal/CompanyModal.vue';
import ApiModal from './modal/ApiModal.vue';
import * as ManageSystemCss from '@/assets/styles/manage/ManageSystemCss.js';

import Plus from 'vue-material-design-icons/Plus.vue';
import AccountGroup from 'vue-material-design-icons/AccountGroup.vue';
import OfficeBuilding from 'vue-material-design-icons/OfficeBuilding.vue';
import ShareVariant from 'vue-material-design-icons/ShareVariant.vue';
import KeyVariant from 'vue-material-design-icons/KeyVariant.vue';
import Magnify from 'vue-material-design-icons/Magnify.vue';

export default {
    name: 'ManageSystem',

    components: {
        ManageSidebar,
        ManagerModal,
        CompanyModal,
        ApiModal,
        ...ManageSystemCss,
        Plus, AccountGroup, OfficeBuilding, ShareVariant, KeyVariant, Magnify
    },

    data() {
        return {
            // 담당자 목록 조회
            showManagerModal: false,
            isEditMode: false,
            managerForm: {},
            managerSearchQuery: '',
            managers: [],

            // 회사 정보 데이터 (로컬 스토리지에서 가져옴)
            companyInfo: {},
            // 회사 정보 모달
            showCompanyModal: false,

            // 공유 업체 데이터
            partners: [],

            // API 모달
            showApiModal: false,
            // API 상태 관리
            hasRegisteredApi: false,
            registeredApiList: [],

            companyCount: 8,
            branchCount: 15,
            partnerCount: 24,
            activePartnerCount: 22,
            apiKeyCount: 6,
            activeApiCount: 5,
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        filteredManagers() {
            if (!this.managers || this.managers.length === 0) {
                return [];
            }

            let filtered = [...this.managers];

            // 검색어가 있는 경우에만 필터링 적용
            if (this.managerSearchQuery && this.managerSearchQuery.trim() !== '') {
                const searchTerm = this.managerSearchQuery.trim().toLowerCase();
                filtered = filtered.filter(manager =>
                    manager.name?.toLowerCase().includes(searchTerm) ||
                    manager.userId?.toLowerCase().includes(searchTerm) ||
                    manager.department?.toLowerCase().includes(searchTerm)
                );
            }

            // 대표가 맨 첫줄에 오도록 정렬
            filtered.sort((a, b) => {
                // 대표인 경우 맨 앞으로
                if (a.department === '대표') return -1;
                if (b.department === '대표') return 1;

                // 나머지는 기존 순서 유지 (created_at 기준)
                return 0;
            });

            return filtered;
        },
    },

    async mounted() {
        await this.getManagers();
        this.loadCompanyInfo();
        this.loadCompanyImages(this.$globalState.company_key);
        await this.loadApiStatus();
        await this.loadSharedCompanies();
    },

    methods: {
        async loadApiStatus() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getApiList`,
                    {
                        params: {
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.success) {
                    this.registeredApiList = response.data.data || [];
                    this.hasRegisteredApi = this.registeredApiList.length > 0;
                }
            } catch (error) {
                console.error('API 상태 로드 오류:', error);
                this.registeredApiList = [];
                this.hasRegisteredApi = false;
            }
        },

        handleSearch() {
            console.log('검색 실행됨 - 검색어:', this.managerSearchQuery);
        },

        openApiModal() {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: 'API 등록 및 관리는 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.showApiModal = true;
        },

        closeApiModal() {
            this.showApiModal = false;
        },

        // API 상태 업데이트 처리 (모달에서 호출)
        async handleApiUpdated() {
            await this.loadApiStatus();
        },

        // 이미지 업로드 열기
        openImageUpload(imageType) {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '이미지 등록은 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }


            this.$nextTick(() => {
                const tempInput = document.createElement('input');
                tempInput.type = 'file';
                tempInput.accept = '.jpg,.jpeg,.png';
                tempInput.style.display = 'none';

                // change 이벤트 리스너 추가
                tempInput.addEventListener('change', (event) => {
                    this.handleImageUpload(imageType, event);
                    // 임시 요소 제거
                    document.body.removeChild(tempInput);
                });

                document.body.appendChild(tempInput);
                tempInput.click();
            });
        },


        // 이미지 업로드 처리
        async handleImageUpload(imageType, event) {
            const file = event.target.files[0];
            if (!file) return;

            try {
                const companykey = this.$globalState.company_key;

                if (!companykey) {
                    this.$swal.fire('오류', '회사 정보를 찾을 수 없습니다.', 'error');
                    return;
                }

                if (window.electronAPI) {
                    // 일렉트론 환경에서는 electronAPI.uploadFile 사용
                    console.log('일렉트론 환경에서 파일 업로드');

                    const formData = {
                        image: {
                            isFile: true,
                            fileName: file.name,
                            fileType: file.type,
                            fileData: await this.fileToBase64(file)
                        },
                        companykey: companykey,
                        imageType: imageType
                    };

                    const response = await window.electronAPI.uploadFile('companyImage', formData);

                    if (response.success) {
                        // 이미지 업로드 성공 후 새로고침
                        await this.loadCompanyImages(companykey, imageType);
                        this.$swal.fire('성공', '이미지가 업로드되었습니다.', 'success');
                    } else {
                        this.$swal.fire('오류', response.message || '이미지 업로드에 실패했습니다.', 'error');
                    }
                } else {
                    // 개발 환경에서는 직접 axios로 업로드
                    console.log('개발 환경에서 파일 업로드');

                    const formData = new FormData();
                    formData.append('image', file);
                    formData.append('companykey', companykey);
                    formData.append('imageType', imageType);

                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/uploadCompanyImage`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    );

                    if (response.data.success) {
                        // 이미지 업로드 성공 후 새로고침
                        await this.loadCompanyImages(companykey, imageType);
                        this.$swal.fire('성공', '이미지가 업로드되었습니다.', 'success');
                    } else {
                        this.$swal.fire('오류', response.data.message || '이미지 업로드에 실패했습니다.', 'error');
                    }
                }

            } catch (error) {
                console.error('이미지 업로드 오류:', error);
                this.$swal.fire('오류', '이미지 업로드 중 오류가 발생했습니다.', 'error');
            }
        },

        // 회사 이미지 삭제
        async deleteCompanyImage(imageType) {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '이미지 삭제는 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            try {
                const companykey = this.$globalState.company_key;
                if (!companykey) {
                    this.$swal.fire('오류', '회사 정보를 찾을 수 없습니다.', 'error');
                    return;
                }

                const result = await this.$swal.fire({
                    title: '이미지 삭제',
                    text: '정말로 이 이미지를 삭제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6'
                });

                if (result.isConfirmed) {
                    // 백엔드에서 이미지 삭제 API 호출
                    const response = await this.$axios.delete(
                        `${this.$apiUrls.hanqmedi}/deleteCompanyImage/${companykey}/${imageType}`
                    );

                    if (response.data.success) {
                        // 로컬 상태에서 이미지 URL 제거
                        const imageUrlField = this.getImageUrlField(imageType);
                        this.companyInfo[imageUrlField] = null;

                        this.$swal.fire('성공', '이미지가 삭제되었습니다.', 'success');
                    } else {
                        this.$swal.fire('오류', response.data.message || '이미지 삭제에 실패했습니다.', 'error');
                    }
                }
            } catch (error) {
                console.error('이미지 삭제 오류:', error);
                this.$swal.fire('오류', '이미지 삭제 중 오류가 발생했습니다.', 'error');
            }
        },

        // 이미지 URL 필드명 반환
        getImageUrlField(imageType) {
            switch (imageType) {
                case 'stamp':
                    return 'stamp_image_url';
                case 'business_card':
                    return 'business_card_image_url';
                case 'logo':
                    return 'logo_image_url';
                default:
                    return 'image_url';
            }
        },

        // 파일을 Base64로 변환
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },

        // 공유업체 목록 로드
        async loadSharedCompanies() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const companykey = userInfo?.company_key;

                if (!companykey) {
                    console.log('회사키를 찾을 수 없음');
                    this.partners = [];
                    return;
                }

                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getSharedCompanies`,
                    {
                        params: { companykey }
                    }
                );

                if (response.data.success) {
                    this.partners = response.data.data || [];
                } else {
                    this.partners = [];
                }
            } catch (error) {
                console.error('공유업체 목록 로드 실패:', error);
                this.partners = [];
            }
        },

        // 공유업체 삭제
        async deletePartner(partnerId) {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '공유업체 삭제는 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            try {
                const result = await this.$swal.fire({
                    title: '공유업체 삭제',
                    text: '정말로 이 공유업체를 삭제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6'
                });

                if (result.isConfirmed) {
                    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                    const companykey = userInfo?.company_key;

                    if (!companykey) {
                        this.$swal.fire('오류', '회사 정보를 찾을 수 없습니다.', 'error');
                        return;
                    }

                    // URL 파라미터로 데이터 전송
                    const response = await this.$axios.delete(
                        `${this.$apiUrls.hanqmedi}/deleteSharedCompany/${companykey}/${partnerId}`
                    );

                    if (response.data.success) {
                        this.$swal.fire('성공', '공유업체가 삭제되었습니다.', 'success');
                        await this.loadSharedCompanies(); // 목록 새로고침
                    } else {
                        this.$swal.fire('오류', response.data.message || '공유업체 삭제에 실패했습니다.', 'error');
                    }
                }
            } catch (error) {
                console.error('공유업체 삭제 오류:', error);
                let errorMessage = '공유업체 삭제 중 오류가 발생했습니다.';

                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                this.$swal.fire('오류', errorMessage, 'error');
            }
        },

        // 공유업체등록/요청 모달
        openSharedCompanyModal() {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '공유업체 등록은 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.$swal.fire({
                title: '<div style="font-size: 24px; font-weight: 700; color: #0f4c5c; margin-bottom: 10px;">공유 업체 등록</div>',
                html: `
                    <div class="swal-form-container">
                        <div class="form-group">
                            <input id="swal-partnerSearch" class="form-input" style="height: 30px; width: 300px;" placeholder="업체명이나 기관기호를 입력하세요" required>
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: '등록',
                cancelButtonText: '취소',
                confirmButtonColor: '#0f4c5c',
                width: '500px',
                customClass: {
                    container: 'swal-partner-modal'
                },
                preConfirm: () => {
                    const searchTerm = document.getElementById('swal-partnerSearch').value.trim();

                    if (!searchTerm) {
                        this.$swal.showValidationMessage('검색어는 필수 입력 항목입니다.');
                        return false;
                    }

                    return { searchTerm };
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    this.registerSharedCompany(result.value.searchTerm);
                }
            });
        },

        // 공유업체 등록 후 목록 새로고침
        async registerSharedCompany(searchTerm) {
            try {
                // 로컬스토리지에서 company_key 가져오기
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                const companykey = userInfo?.company_key;

                if (!companykey) {
                    this.$swal.fire('오류', '회사 정보를 찾을 수 없습니다.', 'error');
                    return;
                }

                console.log('공유업체 등록 시도:', { companykey, searchTerm }); // 디버깅용

                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/registerSharedCompany`,
                    {
                        companykey: companykey,
                        searchTerm: searchTerm
                    }
                );

                if (response.data.success) {
                    const result = response.data.data;

                    if (result.isAlreadyShared) {
                        this.$swal.fire({
                            title: '이미 공유 중',
                            text: '해당 업체들은 이미 공유 중입니다.',
                            icon: 'info',
                            confirmButtonText: '확인'
                        });
                    } else {
                        this.$swal.fire({
                            title: '공유업체 등록 완료',
                            text: response.data.message,
                            icon: 'success',
                            confirmButtonText: '확인'
                        });
                    }

                    // 공유업체 목록 새로고침
                    await this.loadSharedCompanies();
                } else {
                    this.$swal.fire({
                        title: '등록 실패',
                        text: response.data.message || '공유업체 등록에 실패했습니다.',
                        icon: 'error',
                        confirmButtonText: '확인'
                    });
                }
            } catch (error) {
                console.error('공유업체 등록 오류:', error);
                let errorMessage = '공유업체 등록 중 오류가 발생했습니다.';

                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                this.$swal.fire({
                    title: '등록 실패',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            }
        },

        // 상태 텍스트 변환
        getStatusText(status) {
            const statusMap = {
                200: '승인',
                400: '미승인'
            };
            return statusMap[status] || status;
        },

        // 상태 스타일 클래스
        getStatusClass(status) {
            return status === 200 ? 'status-approved' : 'status-pending';
        },

        // 회사 정보 로드
        async loadCompanyInfo() {
            try {
                const companykey = this.$globalState.company_key;
                if (!companykey) {
                    console.log('회사키를 찾을 수 없음');
                    this.companyInfo = {};
                    return;
                }

                const response = await this.$axios({
                    method: 'get',
                    url: `${this.$apiUrls.hanqmedi}/getCompanyInfo?companykey=${companykey}`
                });

                if (response.data.success) {
                    this.companyInfo = response.data.data;
                } else {
                    this.companyInfo = {};
                }
            } catch (error) {
                console.error('회사 정보 로드 중 오류:', error);
                this.companyInfo = {};
            }
        },

        // 회사 이미지들 로드
        async loadCompanyImages(companykey) {
            try {
                const imageTypes = ['stamp', 'business_card', 'logo'];

                for (const imageType of imageTypes) {
                    try {
                        const response = await this.$axios({
                            method: 'get',
                            url: `${this.$apiUrls.hanqmedi}/getCompanyImage/${companykey}/${imageType}`
                        });

                        if (response.data.success && response.data.data.imageUrl) {
                            const imageUrlField = this.getImageUrlField(imageType);
                            this.companyInfo[imageUrlField] = response.data.data.imageUrl;
                        }
                    } catch (imageError) {
                        console.log(`${imageType} 이미지 로드 실패:`, imageError.message);
                        // 이미지가 없는 경우는 정상적인 상황이므로 에러로 처리하지 않음
                    }
                }
            } catch (error) {
                console.error('회사 이미지 로드 중 오류:', error);
            }
        },

        // 회사 정보 수정
        editCompany() {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '회사 정보 수정은 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.showCompanyModal = true;
        },

        // 회사 정보 저장 완료 처리
        async handleCompanySaved(formData) {
            // 회사 정보 새로고침
            await this.loadCompanyInfo();
            // 회사 이미지들도 함께 새로고침
            await this.loadCompanyImages(this.$globalState.company_key);
        },

        // 회사 정보 모달 닫기
        closeCompanyModal() {
            this.showCompanyModal = false;
        },

        // 담당자 목록 조회
        async getManagers() {
            try {
                const companykey = this.$globalState.company_key;

                if (!companykey) {
                    console.log('회사 정보를 찾을 수 없음');
                    return;
                }

                // 검색어가 있는 경우에만 search 파라미터 전달
                const params = { companykey };
                if (this.managerSearchQuery && this.managerSearchQuery.trim() !== '') {
                    params.search = this.managerSearchQuery.trim();
                }

                const response = await this.$axios({
                    method: 'get',
                    url: `${this.$apiUrls.hanqmedi}/getManagers`,
                    params: params
                });

                if (response.data.success) {
                    this.managers = response.data.data;
                } else {
                    this.managers = [];
                }
            } catch (error) {
                console.error('담당자 목록 조회 실패:', error);
                this.managers = [];
            }
        },

        // 담당자 추가 모달
        openAddManagerModal() {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '담당자 추가는 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.isEditMode = false;
            this.managerForm = {};
            this.showManagerModal = true;
        },

        // 담당자 수정 모달
        editManager(manager) {
            if (!this.checkAdminPermission()) {
                this.$swal.fire({
                    title: '권한 없음',
                    text: '담당자 수정은 관리자만 가능합니다.',
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.isEditMode = true;
            this.managerForm = { ...manager };
            this.showManagerModal = true;
        },

        // 권한 확인
        checkAdminPermission() {
            try {
                const userInfo = localStorage.getItem('userInfo');
                if (userInfo) {
                    const parsedInfo = JSON.parse(userInfo);
                    return parsedInfo.role === 'admin';
                }
                return false;
            } catch (error) {
                console.error('사용자 권한 확인 중 오류:', error);
                return false;
            }
        },

        // 담당자 저장 완료 처리
        async handleManagerSaved(formData) {
            await this.getManagers();
        },

        async handleManagerDeleted(managerId) {
            await this.getManagers();
        },

        // 모달 닫기
        closeManagerModal() {
            this.showManagerModal = false;
            this.managerForm = {};
        },

        formatNumber(number) {
            if (!number) return '미등록';
            return number.toString().replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
        },
    }
}
</script>

<style scoped>
.blurred {
    filter: blur(8px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}

.status-approved {
    color: #28a745 !important;
    font-weight: 600;
}

.status-pending {
    color: #6c757d !important;
    font-weight: 600;
}
</style>